<template>
  <div>
    <v-row>
      <v-col cols="12" style="height: 68px" v-if="error" class="my-4" >
        <LoginError :error="error" />
      </v-col>
    </v-row>
    <v-row align="center" :class="error ? '' : 'mt-16'">
      <v-col cols="auto">
        <v-btn variant="plain" @click="goToEmail(); clearError(); password = ''" icon><v-icon color="primary">mdi-arrow-left</v-icon></v-btn>
      </v-col>
      <v-col class="text-center">
        <span class="text-primary text-body-1 font-weight-bold" v-if="activated">Enter your password</span>
        <span class="text-primary text-body-1 font-weight-bold" v-else-if="!activated && !isNew">Enter your registration code</span>
        <span class="text-primary text-body-1 font-weight-bold" v-if="!activated && isNew">Enter your new password</span>
      </v-col>
      <v-col cols="auto">
        <v-sheet width="36" height="36" color="transparent"></v-sheet>
      </v-col>
    </v-row>

    <v-row no-gutters class="mt-1" style="height: 200px">
      <v-col>
        <div class="d-flex flex-column justify-center">
          <v-form v-if="!isNew" @submit.prevent="checkPassword(password)">
            <v-text-field
              autofocus
              v-if="activated"
              class="custom-field"
              :placeholder="'Password'"
              persistent-placeholder
              variant="underlined"
              :append-icon="value ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="() => (value = !value)"
              :type="value ? 'password' : 'text'"
              v-model="password"
              :rules="[v => !!v && !!v.length]"
              @update:modelValue="clearError"
              autocomplete="current-password">
            </v-text-field>
            <v-text-field
              v-else
              class="custom-field"
              placeholder="Registration code"
              persistent-placeholder
              variant="underlined"
              bg-color="#ffffff"
              v-model="code"
              @update:modelValue="clearError"
              autocomplete="new-password">
            </v-text-field>
          </v-form>

          <v-form v-if="isNew" v-model="passwordValid" ref="passwordForm" @submit.prevent="checkPassword(password)">
            <v-text-field
                autofocus
                class="custom-field"
                placeholder="New password"
                persistent-placeholder
                variant="underlined"
                bg-color="#ffffff"
                :append-icon="value['new'] ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="() => (value['new'] = !value['new'])"
                :type="value['new'] ? 'password' : 'text'"
                @update:modelValue="$refs.repeatPasswordForm.validate()"
                v-model="password"
                :rules="[
                    (v) => (v && v.length >= 6) || 'Password must be at least 6 characters long.',
                    (v) => (v && v.toUpperCase() !== v) || 'Password must contain a lowercase character.',
                    (v) => (v && v.toLowerCase() !== v) || 'Password must contain an uppercase character.',
                    (v) => (v && /\d/.test(v)) || 'Password must contain a number.',
                    (v) => (v && symbols.test(v)) || 'Password must contain a special character.',
                ]"
                autocomplete="new-password"></v-text-field>
          </v-form>
          <v-form v-if="isNew" v-model="repeatValid" ref="repeatPasswordForm" @submit.prevent="checkPassword(password)">
            <v-text-field
                class="custom-field"
                placeholder="Repeat new password"
                persistent-placeholder
                variant="underlined"
                bg-color="#ffffff"
                :append-icon="value['repeat'] ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="() => (value['repeat'] = !value['repeat'])"
                :type="value['repeat'] ? 'password' : 'text'"
                v-model="repeatedPassword"
                @update:modelValue="$refs.repeatPasswordForm.validate()"
                :rules="[
                    (v) => !passwordValid || v === password || 'Passwords do not match.'
                ]"
                autocomplete="new-password"></v-text-field>
          </v-form>
        </div>
        <v-col class="text-center">
          <a @click="goToForgotPassword()" class="signup-link" v-if="activated">Forgot your password?</a>
          <div v-else-if="!isNew" class="text-white">Don't have your registration code? <br>  <a @click="goToResendRegistration()" class="signup-link">Resend it.</a> </div>
        </v-col>
      </v-col>
      <v-col cols="12" class="d-flex justify-center" :class="!error ? '' : 'mb-4'">
        <ActionButton variant="flat" width="95" @click="checkPassword(password)" text="Log in" color="primary" :loading="loading" :disabled="confirmedDisabled || loading"></ActionButton>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import {Component, Vue, toNative, Prop, Watch, Emit} from "vue-facing-decorator";
import ActionButton from "@/components/ActionButton.vue";
import LoginError from "@/components/LoginError.vue";
@Component({
  components: {LoginError, ActionButton}
})
class PasswordPrompt extends Vue {
  @Prop() private error!: string;
  @Prop() private activated!: boolean;
  @Prop({default: false}) private isNew!: boolean;
  @Prop() private queryCode!: string;
  @Prop() private loading!: boolean;
  private symbols = /^(?=.*[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ])/;
  private code = '';
  private password = '';
  private repeatedPassword = '';
  private value = {
    repeat: true,
    new: true,
  };
  private passwordValid = true;
  private repeatValid = true;

  private get confirmedDisabled() {
    if (this.isNew) {
      if (this.activated) {
        return !this.code;
      } else {
        return !this.passwordValid || !this.repeatValid || !this.password || !this.repeatedPassword;
      }
    } else {
      return !this.password;
    }
  }

  @Emit()
  private clearError() {
    return true;
  }
  @Emit()
  private goToEmail() {
    return true;
  }
  @Emit()
  private goToForgotPassword() {
    return true;
  }
  @Emit()
  private goToResendRegistration() {
    return true;
  }
  @Emit()
  private checkPassword(password: string): string {
    return password;
  }
  @Watch('queryCode', {immediate: true})
  private onQueryCodeUpdate() {
    this.code = this.queryCode
  }

  @Watch('code', {immediate: true})
  private onCodeUpdate() {
    this.password = this.code
  }

}

export default toNative(PasswordPrompt);
</script>

<style scoped>

</style>